import { AddCardModal } from '@/components/payment/AddCardModal';
import { useLanguage } from '@/contexts/AppContext';
import { translations, addCardModalTranslations } from './payment-method/constants';
import { useSavedCards } from '@/hooks/useSavedCards';
import { PaymentMethodHeader } from './payment-method/PaymentMethodHeader';
import { EmptyCardList } from './payment-method/EmptyCardList';
import { CardList } from './payment-method/CardList';
import { AddCardButton } from './payment-method/AddCardButton';
import { DeleteConfirmDialog } from './payment-method/DeleteConfirmDialog';
import type { PaymentMethodTranslations } from './payment-method/types';
import type {
  BloominCardRegistrationPayload,
  CardRegistrationSession,
} from '@/lib/api/types';
import React, { useState } from 'react';

export function PaymentMethodPage({ onBack }: { onBack: () => void }) {
  const { language } = useLanguage();
  const t = translations[language] as PaymentMethodTranslations;
  const addCardT = addCardModalTranslations[language];

  const {
    cards,
    isLoading,
    setDefault,
    deleteCard,
    createCheckout,
    confirmBloominRegistration,
    isCreatingCheckout,
    isConfirming,
    error,
  } = useSavedCards({ autoFetch: true });

  const [showNewCardModal, setShowNewCardModal] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<CardRegistrationSession | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  const handleSetAsMain = async (cardId: string) => {
    try {
      await setDefault(Number(cardId));
    } catch (err) {
      setPageError(err instanceof Error ? err.message : '기본 카드 설정에 실패했습니다');
      throw err;
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    try {
      await deleteCard(Number(cardId));
      setDeleteConfirmId(null);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : '카드 삭제에 실패했습니다');
      setDeleteConfirmId(null);
      throw err;
    }
  };

  const handleAddCard = async () => {
    if (isCreatingCheckout) return; // 중복 클릭 방지
    try {
      const checkoutData = await createCheckout();
      setCheckout(checkoutData);
      setShowNewCardModal(true);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : '카드 등록을 시작할 수 없습니다');
      throw err;
    }
  };

  const handleBloominCheckoutComplete = async (
    cardPayload: BloominCardRegistrationPayload,
    nickname?: string
  ) => {
    try {
      await confirmBloominRegistration(cardPayload, nickname);
      setCheckout(null);
      setShowNewCardModal(false);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : '카드 등록 확인에 실패했습니다');
      throw err;
    }
  };

  // 로딩 중이거나 에러가 있으면 빈 상태 표시
  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted pb-20">
        <PaymentMethodHeader t={t} onBack={onBack} />
        <div className="pt-[70px] px-4 py-5">
          <div className="flex items-center justify-center py-12">
            <span className="text-muted-foreground">{t.loading}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-muted pb-20">
        <PaymentMethodHeader t={t} onBack={onBack} />
        <div className="pt-[70px] px-4 py-5">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-destructive mb-2">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-primary hover:underline text-sm"
            >
              {t.retry}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted pb-20">
      <PaymentMethodHeader t={t} onBack={onBack} />

      <div className="pt-[70px] px-4 py-5 pr-[16px] pb-[20px] pl-[16px]">
        {cards.length === 0 ? (
          <EmptyCardList t={t} />
        ) : (
          <CardList
            cards={cards.map(card => ({
              id: card.id.toString(),
              type: card.card_brand.toLowerCase() as 'visa' | 'mastercard' | 'amex' | 'discover',
              lastFour: card.card_last_four,
              holder: card.card_holder || '',
              expiry: card.expiry_display,
              isDefault: card.is_default,
            }))}
            t={t}
            onSetAsMain={handleSetAsMain}
            onDeleteClick={setDeleteConfirmId}
          />
        )}

        <AddCardButton t={t} onClick={handleAddCard} />
      </div>

      <AddCardModal
        isOpen={showNewCardModal}
        checkout={checkout}
        isCreatingCheckout={isCreatingCheckout}
        isConfirming={isConfirming}
        onClose={() => {
          setShowNewCardModal(false);
          setCheckout(null);
        }}
        onCreateCheckout={handleAddCard}
        onConfirmBloominRegistration={handleBloominCheckoutComplete}
        t={addCardT}
      />

      <DeleteConfirmDialog
        open={deleteConfirmId !== null}
        t={t}
        onCancel={() => setDeleteConfirmId(null)}
        onConfirm={() => deleteConfirmId && handleDeleteCard(deleteConfirmId)}
      />
    </div>
  );
}
